var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, forwardRef, useId, useEffect } from 'react';
import Spinner from '../Spinner';
import User from '../../assets/icons/User';
import Trash from '../../assets/icons/Trash';
import PenSquare from '../../assets/icons/PenSquare';
import { Modifiers } from '../../helpers/getModifiers';
import { avatarProps, optionsProps } from './modifiders';
import { isFunction } from '../../utils/assertion';
import { colorSchemes, useTheme } from '../../themes';
import * as S from './styles';
var getOnlyKeysProps = Modifiers.getOnlyKeysProps, allAcceptableProps = Modifiers.allAcceptableProps;
var keys = __spreadArray(__spreadArray(__spreadArray(__spreadArray([], allAcceptableProps.spacings, true), allAcceptableProps.sizes, true), allAcceptableProps.colors, true), allAcceptableProps.borders, true);
export var Avatar = forwardRef(function (props, ref) {
    var _a, _b, _c, _d;
    var idProp = props.id, src = props.src, fallback = props.fallback, username = props.username, isUploading = props.isUploading, isUpload = props.isUpload, uploadingText = props.uploadingText, _e = props.uploadingIcon, uploadingIcon = _e === void 0 ? _jsx(Spinner, { size: "medium", color: colorSchemes.blue.pure }) : _e, uploadText = props.uploadText, _f = props.uploadIcon, uploadIcon = _f === void 0 ? _jsx(User, {}) : _f, iconColorProp = props.iconColor, bStyleProp = props.bStyle, uploadingTextProps = props.uploadingTextProps, uploadTextProps = props.uploadTextProps, usernameTextProps = props.usernameTextProps, onChange = props.onChange, onRemove = props.onRemove, rest = __rest(props, ["id", "src", "fallback", "username", "isUploading", "isUpload", "uploadingText", "uploadingIcon", "uploadText", "uploadIcon", "iconColor", "bStyle", "uploadingTextProps", "uploadTextProps", "usernameTextProps", "onChange", "onRemove"]);
    var uuid = useId();
    var theme = useTheme();
    var getContainerProps = getOnlyKeysProps(rest, keys);
    var defProps = avatarProps(theme);
    var _g = useState(src !== null && src !== void 0 ? src : fallback), imageSrc = _g[0], setImageSrc = _g[1];
    var _h = useState(false), imageLoaded = _h[0], setImageLoaded = _h[1];
    function onLoad() {
        setImageLoaded(true);
    }
    function onError() {
        setImageLoaded(true);
        if (!fallback)
            return;
        setImageSrc(fallback);
    }
    var id = idProp || "img-".concat(uuid);
    var iconOptionsProps = optionsProps(rest, theme);
    var children = null;
    var cursor = 'pointer';
    var bStyle = bStyleProp || defProps.bStyle;
    var bWidth = (rest === null || rest === void 0 ? void 0 : rest.bWidth) || defProps.bWidth;
    var bColor = (rest === null || rest === void 0 ? void 0 : rest.bColor) || defProps.bColor;
    var showOptions = false;
    var gap = 0;
    var bgColor = ((rest === null || rest === void 0 ? void 0 : rest.bgColor) || theme.neutrals.pureWhite);
    var iconColor = iconColorProp || defProps.iconColor;
    useEffect(function () {
        setImageSrc(src);
    }, [src]);
    if (isUploading) {
        bColor = colorSchemes.blue.pure;
        cursor = 'wait';
        gap = theme.spacing.quark;
        if (!iconColorProp) {
            iconColor = colorSchemes.blue.pure;
        }
        children = (_jsxs(_Fragment, { children: [uploadingIcon, !!uploadingText && (_jsx(S.UploadingText, __assign({ "data-testid": "avatar-uploading-text" }, uploadingTextProps, { children: uploadingText })))] }));
    }
    else if (!imageSrc && username) {
        showOptions = true;
        if (!bStyleProp) {
            bStyle = 'solid';
        }
        if (!rest.bColor) {
            bColor = colorSchemes.blue.pure;
        }
        if (!rest.bgColor) {
            bgColor = theme.neutrals.gray1;
        }
        children = (_jsx(_Fragment, { children: _jsx(S.UsernameText, __assign({}, usernameTextProps, { "data-testid": "avatar-username-text" }, { children: username.charAt(0).toUpperCase() })) }));
    }
    else if (imageSrc) {
        showOptions = true;
        bStyle = 'solid';
        bColor = colorSchemes.blue.pure;
        bgColor =
            imageSrc === fallback ? theme.neutrals.gray1 : theme.neutrals.pureWhite;
        children = (_jsx(_Fragment, { children: _jsx(S.Image, __assign({ ref: ref, onError: onError, src: imageSrc, onLoad: onLoad, imageLoaded: imageLoaded, "data-testid": "avatar-image" }, rest)) }));
    }
    else if (isUpload) {
        bWidth = theme.borderWidth.hairline;
        gap = theme.spacing.nano;
        children = (_jsxs(_Fragment, { children: [uploadIcon, !!uploadText && (_jsx(S.UploadText, __assign({}, uploadTextProps, { htmlFor: id, "data-testid": "avatar-upload-text" }, { children: uploadText })))] }));
    }
    var mustNotClickOnAvatar = isUploading || showOptions;
    if (showOptions) {
        cursor = 'normal';
        if (!isFunction(onChange) && !isFunction(onRemove))
            showOptions = false;
    }
    if (!children)
        return null;
    return (_jsxs(S.Container, __assign({ showOptions: showOptions, "data-testid": "avatar-container" }, __assign(__assign({}, getContainerProps), __assign(__assign({}, rest), { bStyle: bStyle, bWidth: bWidth, bColor: bColor, bgColor: bgColor })), { children: [_jsx(S.Wrapper, __assign({ htmlFor: mustNotClickOnAvatar ? '' : id, "data-testid": "avatar-wrapper" }, __assign({ bgColor: bgColor, gap: gap, cursor: cursor, iconColor: iconColor }, rest), { children: children })), _jsxs(S.IconWrapper, __assign({ id: "icon-wrapper" }, { children: [isFunction(onChange) && (_jsx(S.IconCircle, __assign({ htmlFor: id, "data-testid": "avatar-edit-icon" }, iconOptionsProps.edit, { children: (_b = (_a = iconOptionsProps.edit) === null || _a === void 0 ? void 0 : _a.icon) !== null && _b !== void 0 ? _b : _jsx(PenSquare, {}) }))), isFunction(onRemove) && (_jsx(S.RemoveIcon, __assign({ onClick: onRemove, "data-testid": "avatar-remove-icon" }, iconOptionsProps.remove, { children: (_d = (_c = iconOptionsProps.remove) === null || _c === void 0 ? void 0 : _c.icon) !== null && _d !== void 0 ? _d : _jsx(Trash, {}) })))] })), _jsx("input", { id: id, type: "file", style: { display: 'none' }, onChange: onChange, "data-testid": "avatar-hidden-input" })] })));
});
